import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Heart,
  Activity,
  FlaskConical,
  ShieldPlus,
  Brain,
} from 'lucide-react';

import { useTranslation } from 'react-i18next';
import authService from '../../services/authService';
import { useApi } from '../../hooks/useApi';
import PartnerSignIn from '../../components/PartnerSignIn/PartnerSignIn';
import './ResetPassword.css';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { token = '' } = useParams<{ token: string }>();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [success, setSuccess] = useState(false);
  const [localError, setLocalError] = useState('');

  const {
    execute: resetPassword,
    loading,
    error,
  } = useApi(authService.confirmPasswordReset);

  const features = [
    {
      icon: Activity,
      text: t('auth:features.healthRecords'),
    },
    {
      icon: ShieldPlus,
      text: t('auth:features.insurance'),
    },
    {
      icon: FlaskConical,
      text: t('auth:features.smartReports'),
    },
    {
      icon: Heart,
      text: t('auth:features.womensHealth'),
    },
    {
      icon: Brain,
      text: t('auth:features.aiAssessments'),
    },
  ];

    const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
    ) => {
    e.preventDefault();

    setLocalError('');

    if (!newPassword.trim()) {
        setLocalError('Please enter a new password.');
        return;
    }

    if (!confirmPassword.trim()) {
        setLocalError('Please confirm your password.');
        return;
    }

    if (newPassword !== confirmPassword) {
        setLocalError('Passwords do not match.');
        return;
    }

    const response = await resetPassword({
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
    });

    if (response) {
        setSuccess(true);
    }
    };

  return (
    <div className="login-page">

      <div className="login-bg-shape shape-1" />
      <div className="login-bg-shape shape-2" />
      <div className="login-bg-shape shape-3" />

      <div className="login-container">

        <div className="login-left">

          <div className="login-branding">

            <div className="login-logo-mark">
              <img src="/images/logo.svg" alt="Infinicare" style={{ height: 100 }} />
            </div>

            <p className="login-tagline">
              {t('auth:tagline')}
            </p>

            <div className="login-features">

              {features.map((feature, index) => {

                const Icon = feature.icon;

                return (

                  <div key={index} className="feature-item" >

                    <div className="feature-icon-wrapper">
                      <Icon size={20} />
                    </div>

                    <span>{feature.text}</span>

                  </div>

                );

              })}

            </div>

          </div>

        </div>

        <div className="login-right">

          <div className="login-form-container">

            <button type="button" className="back-to-roles" onClick={() => navigate('/login')} > <ArrowLeft size={16} />
              Back
            </button>

            <h2 className="login-title">
              Reset Password
            </h2>

            <p className="login-subtitle">
              Create a strong password for your account.
            </p>

            {success ? (
            <>
            <div className="login-success">
                <CheckCircle2 size={20} />

                <div>
                <strong>Password Reset Successful</strong>

                <p>
                    Your password has been updated successfully.
                    <br />
                    You can now sign in using your new password.
                </p>
                </div>
            </div>
            </>
            ) : (
            <>
            {(localError || error) && (
            <div className="login-error">
                <AlertCircle size={18} />
                <span>{localError || error}</span>
            </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">

                <div className="form-group">
                <label>New Password</label>

                <div className="input-wrapper">

                    <Lock size={18} className="input-icon" />

                    <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                    setNewPassword(e.target.value);
                    setLocalError('');
                    }}                    
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    required
                    />

                    <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>

                </div>
                </div>

                <div className="form-group">
                <label>Confirm Password</label>

                <div className="input-wrapper">

                    <Lock size={18} className="input-icon" />

                    <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setLocalError('');
                    }}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    required
                    />

                    <button
                    type="button"
                    className="password-toggle"
                    onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                    }
                    >
                    {showConfirmPassword ? (
                        <EyeOff size={18} />
                    ) : (
                        <Eye size={18} />
                    )}
                    </button>

                </div>
                </div>

                <button
                type="submit"
                className="login-button"
                disabled={loading}                >
                {loading ? (
                    <span className="button-loading">
                    <Loader2 size={20} className="spinner" />
                    Updating Password...
                    </span>
                ) : (
                    'Reset Password'
                )}
                </button>

            </form>
            </>
            )}

            <div className="login-footer">
            <p>
                Remember your password?{' '}
                <Link to="/login" className="register-link">
                Sign In
                </Link>
            </p>
            </div>

            <PartnerSignIn />

          </div>

        </div>

      </div>

    </div>
    );

};

export default ResetPassword;